import { useContext, useState } from "react"

import { 
  DataTable, 
  EllipsisDropdown, 
  EmptyPlaceholder, 
  ErrorPlaceholder,
  EventMenu, 
  EventPageHeader, 
  EventViewsTracker,
  GroupBookingModal,
  Page 
} from "@components"

import { Chip } from "@heroui/react"

import { 
  useLazyQuery, 
  useMutation, 
  useQuery 
} from "@apollo/client"

import { 
  CREATE_BOOKING_SLOT_MUTATION,
  GET_GROUP_SLOTS, 
  GET_REPORT_WAITING_LIST_XLS_DOWNLOAD_LINK,
  GET_WAITING_LIST, 
  GET_WAITING_LIST_SUMMARY 
} from "@graphql"

import {
  downloadFile,
  getGraphQLErrorMessage,
  getSessionFilters,
  humanReadableDuration,
  mySqlToLocal,
  showLoader,
} from "@helpers"

import {
  useColumnManager,
  useSetActiveSidebarLink, 
  useSetCurrentEvent 
} from "@hooks"

import { QueueListIcon } from "@heroicons/react/24/outline"
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/24/solid"

import { 
  ContextualSidebarContext, 
  NotificationContext, 
  UserContext 
} from "@context"

import { useParams } from "react-router-dom"

import { 
  columns, 
  defaultColumns, 
  downloadOptions, 
  filters 
} from "./data"

import { truncate } from "lodash"
import { Stats } from "./Stats"
import WaitingListDetails from "./WaitingListDetails"

const WaitingList = () => {
  const { event: eventId } = useParams()
  const { user } = useContext(UserContext)
  const { notify } = useContext(NotificationContext)
  const { setContextualSidebar } = useContext(ContextualSidebarContext)
  const [currentWaitingListItem, setCurrentWaitingListItem] = useState(null)
  const [isDownloading, setIsDownloading] = useState(false)
  const [isBookingSlot, setIsBookingSlot] = useState(false)
  const [loadingItemId, setLoadingItemId] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [selectedFilters, setSelectedFilters] = useState([])
  const [selectedSessionIds, setSelectedSessionIds] = useState([])
  const [showGroupBookingModal, setShowGroupBookingModal] = useState(false)
  const [sortDescriptor, setSortDescriptor] = useState(null)
  const [refetching, setRefetching] = useState(false)
  const [page, setPage] = useState(1)
  const [perPage] = useState(50)

  useSetActiveSidebarLink("EVENT_REPORTS_WAITING_LIST", "EVENT_REPORTS")
  useSetCurrentEvent(eventId)
  const [selectedColumns, setSelectedColumns] = useColumnManager(columns, "reports.event.waitinglist.columns", defaultColumns)

  const [createBookingSlot] = useMutation(CREATE_BOOKING_SLOT_MUTATION, {
    onCompleted: () => notify({ title: "Success", type: "success", content: "Slot booked successfully", open: true }),
    onError: (errors) => notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while booking the slot."), open: true })
  })

  const summaryData = useQuery(GET_WAITING_LIST_SUMMARY, {
    variables: {
      event_id: eventId,
    },
    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
  })

  const [getWaitingListXlsDownloadLink] = useLazyQuery(GET_REPORT_WAITING_LIST_XLS_DOWNLOAD_LINK, {
    variables: { event_id: eventId },
    fetchPolicy: "network-only",
    onCompleted: data => {
      downloadFile(data.reportWaitingListXls.filename, data.reportWaitingListXls.url)
      setIsDownloading(false)
    },
  })

  const [getGroupSlots] = useLazyQuery(GET_GROUP_SLOTS, { 
    fetchPolicy: "no-cache",
    onError: (errors) => {
      notify({ title: "Error", type: "danger", content: "Cannot fetch group slots", open: true })
    }
  })

  const { error, data, loading, networkStatus, refetch } = useQuery(GET_WAITING_LIST, {
    variables: {
      input: {
        event_id: eventId,
        search_term: searchTerm,
        filter: selectedFilters[0] ?? null,
        session_ids: selectedSessionIds.length > 0 ? selectedSessionIds : user.currentEvent.sessions.map((session) => session.id),
      },
      page,
      first: perPage,
      orderBy: sortDescriptor
        ? [
            {
              column: sortDescriptor.column.toUpperCase(),
              order: sortDescriptor.direction === "ascending" ? "ASC" : "DESC",
            },
          ]
        : [],

    },
    fetchPolicy: "cache-and-network",
    pollInterval: 900000,
  })

  const handleRefetchButtonClicked = () => {
    setRefetching(true)
    summaryData.refetch()
    refetch().then(() => setRefetching(false))
  }

  const handleBookSlot = (slotId, studentId, userId, lessonId, meetingTypeId) => {
    setIsBookingSlot(true)

    return createBookingSlot({
      variables: {
        input: {
          event_id: eventId,
          lesson_id: lessonId,
          slot_id: slotId,
          student_id: studentId,
          user_id: userId,
          quantity: 1,
          meeting_type_id: meetingTypeId
        }
      }
    }).finally((response) => setIsBookingSlot(false))
  }

  const handleCloseGroupBookingModal = (hasMadeBookings) => {
    if (hasMadeBookings) {
      summaryData.refetch()
      refetch()
    }

    setCurrentWaitingListItem(null)
    setShowGroupBookingModal(false)
  }

  const handleDownload = type => {
    if (type === "xls") {
      setIsDownloading(true)
      getWaitingListXlsDownloadLink()
    }
  }

  const handleShowWaitingListDetails = item => {
    setContextualSidebar(prevState => ({
      ...prevState,
      open: true,
      title: `Waiting List Details`,
      subTitle: `Request from ${item.guardian}`,
      content: <WaitingListDetails details={item} />,
      size: "xl",
    }))
  }

  const handleSearchClear = () => {
    setSearchTerm("")
    setPage(1)
  }

  const handleSearchChange = searchTerm => {
    setSearchTerm(searchTerm)
    setPage(1)
  }

  const handleShowGroupBookingModal = async (item) => {
    setLoadingItemId(item.id)
    const response = await getGroupSlots({ variables: { id: item.group_id }})
    const slots = response?.data?.group?.slots
    setLoadingItemId(null)

    if (!slots) return

    setCurrentWaitingListItem({
      ...item, 
      slots: slots.map(slot => ({
        ...slot, 
        starts_at: mySqlToLocal(slot.starts_at), 
        ends_at: mySqlToLocal(slot.ends_at) 
      }))
    })

    setShowGroupBookingModal(true)
  }

  const handleSortChange = sortDescriptor => {
    setSortDescriptor(sortDescriptor)
    setPage(1)
  }

  const handleToggleColumn = selectedColumns => {
    setSelectedColumns(Object.keys(columns).filter(column => selectedColumns.has(column)))
  }

  const handleToggleFilter = selectedFilters => {
    setSelectedFilters([...selectedFilters])
    setPage(1)
  }

  const handleToggleSessionFilter = selectedSessionIds => setSelectedSessionIds([...selectedSessionIds])

  const handleRenderCell = (item, column) => {
    if (column === "actions") {
      const items = [
        { id: 1, handleClick: () => handleShowWaitingListDetails(item), caption: "View Details" },
        { id: 2, handleClick: () => handleShowGroupBookingModal(item), caption: "Book Slot", isDisabled: item.available_slots === 0 || item.has_booked }
      ]

      return <EllipsisDropdown items={items} isLoading={loadingItemId === item.id} />
    }

    if (column === "created_at") {
      return humanReadableDuration(mySqlToLocal(item["created_at"])) + " ago"
    }

    if (column === "available_slots") {
      return (
        <div className="flex justify-center">
          {item["available_slots"] > 0
            ? <Chip className="flex justify-center text-white bg-green-500" aria-hidden="true"> {item["available_slots"]} </Chip>
            : <Chip className="flex justify-center text-white bg-red-500" aria-hidden="true"> {item["available_slots"]} </Chip>
          }
        </div>
      )
    }

    if (column === "booked") {
      return (
        <div className="flex justify-center">
          {item["has_booked"] 
            ? <CheckCircleIcon className="flex justify-center w-8 h-8 text-green-500" aria-hidden="true" /> 
            : <XCircleIcon className="flex justify-center w-8 h-8 text-red-500" aria-hidden="true" />
          }
        </div>
      )
    }
    
    if (column === "comment") {
      return truncate(item["comment"], { length: 30 })
    }

    return item[column]
  }

  const event = user?.currentEvent
  const sessionFilters = event?.sessions ? getSessionFilters(event.sessions) : null
  const defaultSelectedSessionIds = sessionFilters !== null ? Object.keys(sessionFilters) : null
  const defaultMeetingType = event?.meetingTypes && event?.meetingTypes.length === 1 ? event?.meetingTypes[0] : null
  const showLoadingPlaceholder = showLoader(loading, networkStatus, data?.reportWaitingList?.data, refetching);

  return (
    <EventViewsTracker eventId={eventId}>
      <Page>
        <EventPageHeader
          title="Waiting List Report"
          icon={<QueueListIcon className="page-header-icon" />}
          event={user?.currentEvent}
          actions={<EventMenu event={user?.currentEvent} user={user} />}
        />

        <Stats summary={summaryData} />

        {error && <ErrorPlaceholder caption="Error loading waiting list" error={error} />}

        {!error && (
          <DataTable
            columns={columns}
            currentPage={page}
            currentSortOrder={sortDescriptor}
            data={data?.reportWaitingList?.data}
            disallowEmptyFilterSelection={false}
            downloadOptions={downloadOptions}
            filters={filters}
            filtersSelectionMode="single"
            handleDownload={type => handleDownload(type)}
            handlePageChange={page => setPage(page)}
            handleRefetch={() => handleRefetchButtonClicked()}
            handleRenderCell={(item, key) => handleRenderCell(item, key)}
            handleSearchChange={searchTerm => handleSearchChange(searchTerm)}
            handleSearchClear={handleSearchClear}
            handleSortChange={sortDescriptor => handleSortChange(sortDescriptor)}
            handleToggleColumn={selectedColumns => handleToggleColumn(selectedColumns)}
            handleToggleFilter={selectedFilters => handleToggleFilter(selectedFilters)}
            handleToggleSessionFilter={selectedSession => handleToggleSessionFilter(selectedSession)}
            isDownloading={isDownloading}
            isRefetching={refetching}
            loading={showLoadingPlaceholder}
            noDataMessage={<EmptyPlaceholder classNames="mt-2" title="No Data Found" description="No Data found! Try clearing any filters." />}
            paginatorInfo={data?.reportWaitingList?.paginatorInfo}
            searchInputPlaceHolder="Search by group / student / lesson / guardian"
            searchTerm={searchTerm}
            selectedColumns={selectedColumns}
            selectedFilters={selectedFilters}
            sessionFilters={sessionFilters}
            selectedSessions={selectedSessionIds ?? defaultSelectedSessionIds}          
            showDownloadButton={true}
            showFilterCount={true}
            showRefetchButton={true}
          />
        )}

        {showGroupBookingModal && currentWaitingListItem && (
          <GroupBookingModal
            autoClose={true}
            eventId={event.id}
            groupId={currentWaitingListItem.group_id}
            handleBookSlot={(slotId, studentId, userId, lessonId, meetingTypeId) => handleBookSlot(slotId, studentId, userId, lessonId, meetingTypeId)}
            isSaving={isBookingSlot}
            handleClose={(hasMadeBookings) => handleCloseGroupBookingModal(hasMadeBookings)}
            initialSlots={currentWaitingListItem.slots}
            lessonId={currentWaitingListItem.lesson_id}
            defaultMeetingType={defaultMeetingType}
            meetingTypes={event?.meetingTypes || []}
            show={true}
            showAvailableSlots={true}
            title={currentWaitingListItem.group}
            defaultStudentId={currentWaitingListItem.student_id}
            defaultGuardianId={currentWaitingListItem.guardian_id}
          />
        )}
      </Page>
    </EventViewsTracker>
  )
}

export default WaitingList
